// UsersView.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { deleteUser, fetchUsers } from "../utils/usersApi";
import { logAction } from "../utils/logsApi";
import Modal from "./Modal";
import Loading from "./Loading";

const UsersView = ({ users = [], setUsers }) => {
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
  });
  const navigate = useNavigate();
  const userRole = Number(localStorage.getItem("userRole"));

  // Authentication check
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
      return;
    }
  }, [navigate]);

  const openModal = (title, message, type = "info", onConfirm = null) => {
    setModal({ isOpen: true, title, message, type, onConfirm });
  };

  const closeModal = () => {
    setModal({ isOpen: false, title: "", message: "", type: "info", onConfirm: null });
  };

  const handleAddUser = () => {
    navigate("/add-user");
  };

  const validUsers = Array.isArray(allUsers) ? allUsers.filter((user) => user && user.userId) : [];

  const handleDeleteUser = async (user) => {
    openModal(
      "Confirm Deletion",
      `Are you sure you want to delete user ${user.fullName}?`,
      "confirmation",
      async () => {
        try {
          const response = await deleteUser(user);

          if (response.success) {
            openModal("Success", `User ${user.fullName} deleted successfully!`, "info");
            logAction(
              "DELETE_USER",
              localStorage.getItem("userEmail"),
              `Deleted user: ${user.fullName}`,
              user.userId
            );

            const updatedUsers = await fetchUsers();
            setAllUsers(updatedUsers.filter((user) => user && user.userId));
          } else {
            openModal("Error", `Error deleting user: ${response.message}`, "info");
          }
        } catch (error) {
          console.error("Error handling user deletion:", error);
          openModal("Error", "An error occurred while deleting the user.", "info");
        }
      }
    );
  };

  useEffect(() => {
    const fetchAllUsers = async () => {
      setLoading(true);
      try {
        const firstPageResponse = await fetchUsers(1);
        let allData = [...firstPageResponse.users];
        let totalPagesFetched = firstPageResponse.totalPages || 1;

        if (totalPagesFetched > 1) {
          const requests = [];
          for (let i = 2; i <= totalPagesFetched; i++) {
            requests.push(fetchUsers(i));
          }

          const responses = await Promise.all(requests);
          responses.forEach((res) => {
            if (res.users) allData = [...allData, ...res.users];
          });
        }

        setAllUsers(allData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
      setLoading(false);
    };

    fetchAllUsers();
  }, []);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);




  return (
    <div>
      <h2 className="sub-headline">Users</h2>

      {userRole >= 1 && (
        <button className="add-user-button" onClick={handleAddUser}>
          +
        </button>
      )}

      <div className="filter-container">
        <input
          type="text"
          placeholder="🔍 Filter by User Full Name / Email / ID"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="filter-input"
        />
      </div>

      {/* ✅ Show Loading Component While Data is Fetching */}
      {loading ? (
        <Loading message="Loading users..." />
      ) : validUsers.length === 0 ? (
        <p>No users available.</p>
      ) : (
        <table border="1" style={{ margin: "20px auto", width: "80%" }}>
          <thead>
            <tr>
              <th>#</th>
              <th>User ID</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Creation Date</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {validUsers
              .filter((user) =>
                user.fullName &&
                (
                  String(user.fullName).toLowerCase().includes(filterText.toLowerCase()) ||
                  String(user.userId || "").includes(filterText) ||
                  String(user.mail || "").toLowerCase().includes(filterText.toLowerCase())
                )
              )
              .slice((currentPage - 1) * 20, currentPage * 20)
              .map((user, index) => (
                <tr key={user.userId}>
                  <td>{(currentPage - 1) * 20 + index + 1}</td>
                  <td>{user.userId || ""}</td>
                  <td>{user.fullName || ""}</td>
                  <td>{user.mail || ""}</td>
                  <td>
                    {user.joinDate
                      ? (() => {
                        const [day, month, year] = user.joinDate.split("/");
                        const date = new Date(`${year}-${month}-${day}`);
                        return date.toLocaleDateString("en-GB");
                      })()
                      : "N/A"}
                  </td>
                  <td>
                    <button onClick={() => handleDeleteUser(user)}>Delete</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}

      <div className="pagination-controls">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            fontSize: "20px",
            padding: "5px 10px",
            background: "none",
            border: "none",
            cursor: "pointer"
          }}
        >
          &lt;
        </button>
        <span>Page {currentPage} of {Math.ceil(validUsers.filter((user) =>
          user.fullName &&
          (
            String(user.fullName).toLowerCase().includes(filterText.toLowerCase()) ||
            String(user.userId || "").includes(filterText) ||
            String(user.mail || "").toLowerCase().includes(filterText.toLowerCase())
          )
        ).length / 20)}</span>

        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(validUsers.filter((user) =>
            user.fullName &&
            (
              String(user.fullName).toLowerCase().includes(filterText.toLowerCase()) ||
              String(user.userId || "").includes(filterText) ||
              String(user.mail || "").toLowerCase().includes(filterText.toLowerCase())
            )
          ).length / 20)))}
          disabled={currentPage === Math.ceil(validUsers.filter((user) =>
            user.fullName &&
            (
              String(user.fullName).toLowerCase().includes(filterText.toLowerCase()) ||
              String(user.userId || "").includes(filterText) ||
              String(user.mail || "").toLowerCase().includes(filterText.toLowerCase())
            )
          ).length / 20)}
          style={{
            fontSize: "20px",
            padding: "5px 10px",
            background: "none",
            border: "none",
            cursor: "pointer"
          }}
        >
          &gt;
        </button>
      </div>

      <Modal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
      />
    </div>
  );
};

export default UsersView;